import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Star, ExternalLink } from "lucide-react";
import { TESTIMONIALS, GOOGLE_REVIEW_URL } from "@/data/spa";
import { useLang } from "@/i18n/LanguageContext";

const Card = ({ t }) => (
  <div className="w-[340px] sm:w-[400px] mx-3 bg-[#0c100e] border border-white/10 p-8 hover:border-[#c5a059]/40 transition-colors duration-500">
    <div className="flex gap-1 text-[#c5a059] mb-5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={13} fill="currentColor" />
      ))}
    </div>
    <p className="font-serif-display italic text-lg text-[#f0f4f2]/90 leading-relaxed whitespace-normal">
      “{t.quote}”
    </p>
    <div className="mt-6 flex items-center gap-3">
      <span className="w-9 h-9 rounded-full border border-[#c5a059]/50 flex items-center justify-center font-serif-display text-[#c5a059]">
        {t.initial}
      </span>
      <span className="text-xs tracking-[0.2em] uppercase text-[#8a9690]">{t.name}</span>
    </div>
  </div>
);

export const Testimonials = () => {
  const { t } = useLang();
  return (
  <section id="reviews" data-testid="testimonials-section" className="py-24 lg:py-40 overflow-hidden">
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="max-w-7xl mx-auto px-6 lg:px-10 mb-16"
    >
      <div className="flex items-center gap-4 mb-6">
        <span className="h-px w-12 bg-[#c5a059]" />
        <span className="text-[11px] tracking-[0.4em] uppercase text-[#c5a059]">{t.reviews.label}</span>
      </div>
      <h2 className="font-serif-display font-medium text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#f0f4f2] max-w-2xl">
        {t.reviews.title.pre}<span className="italic text-[#c5a059]">{t.reviews.title.it}</span>{t.reviews.title.post}
      </h2>
    </motion.div>

    <div className="space-y-6">
      <Marquee speed={28} gradient={false} pauseOnHover>
        {TESTIMONIALS.slice(0, 5).map((t, i) => (
          <Card key={i} t={t} />
        ))}
      </Marquee>
      <Marquee speed={22} gradient={false} pauseOnHover direction="right">
        {TESTIMONIALS.slice(5).map((t, i) => (
          <Card key={i} t={t} />
        ))}
      </Marquee>
    </div>

    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="max-w-7xl mx-auto px-6 lg:px-10 mt-16"
    >
      <div
        data-testid="review-invite"
        className="border border-white/10 bg-[#0c100e]/60 px-8 py-8 lg:px-12 flex flex-col sm:flex-row sm:items-center gap-6"
      >
        <div className="flex gap-1 text-[#c5a059] shrink-0">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} size={16} fill="currentColor" />
          ))}
        </div>
        <div className="flex-1">
          <h3 className="font-serif-display text-2xl text-[#f0f4f2]">{t.review.title}</h3>
          <p className="mt-1 text-sm text-[#8a9690] font-light max-w-xl">{t.review.text}</p>
        </div>
        <a
          href={GOOGLE_REVIEW_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="review-invite-button"
          className="shrink-0 inline-flex items-center gap-3 border border-[#c5a059]/50 text-[#c5a059] hover:bg-[#c5a059] hover:text-[#050706] px-7 py-3.5 text-xs tracking-[0.2em] uppercase transition-colors duration-300"
        >
          <ExternalLink size={13} /> {t.review.cta}
        </a>
      </div>
    </motion.div>
  </section>
  );
};
